import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Steps,
  Card,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Table,
  Checkbox,
  Descriptions,
  Tag,
  Space,
  Row,
  Col,
  message,
  Spin,
  Result,
  Typography,
  Divider,
} from 'antd';
import { PlusOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import {
  getOrganizations,
  getUsers,
  getSims,
  createAsset,
  createUser,
  assignSimToAsset,
  getTemplates,
  Organization,
  User,
  UnifiedSim,
  YachtTemplate,
} from '../../services/adminService';
import LookupSelect from '../../components/LookupSelect';

const { Title, Text, Paragraph } = Typography;

interface CrewRow {
  key: string;
  role: string;
  department: string;
  count: number;
}

interface WizardData {
  template: YachtTemplate | null;
  yachtName: string;
  organizationId: number | null;
  assetType: string;
  crewStructure: CrewRow[];
  selectedSimIds: string[];
  captainManagesSims: boolean;
  captainProtection: boolean;
  captainMode: 'new' | 'existing';
  captainFirstName: string;
  captainLastName: string;
  captainEmail: string;
  existingCaptainId: number | null;
}

const initialWizardData: WizardData = {
  template: null,
  yachtName: '',
  organizationId: null,
  assetType: '',
  crewStructure: [],
  selectedSimIds: [],
  captainManagesSims: false,
  captainProtection: false,
  captainMode: 'new',
  captainFirstName: '',
  captainLastName: '',
  captainEmail: '',
  existingCaptainId: null,
};

let crewKeyCounter = 0;
function nextCrewKey(): string {
  crewKeyCounter += 1;
  return `crew-${crewKeyCounter}`;
}

const YachtOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>(initialWizardData);
  const [templates, setTemplates] = useState<YachtTemplate[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [sims, setSims] = useState<UnifiedSim[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingSims, setLoadingSims] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(false);
  const [createdAssetId, setCreatedAssetId] = useState<number | null>(null);

  const [detailsForm] = Form.useForm();
  const [captainForm] = Form.useForm();

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const data = await getTemplates();
        setTemplates(data);
      } catch {
        // Templates endpoint may not exist yet
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  // Fetch organizations on mount
  useEffect(() => {
    const fetchOrgs = async () => {
      setLoadingOrgs(true);
      try {
        const data = await getOrganizations();
        setOrganizations(data);
      } catch {
        // silently fail
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrgs();
  }, []);

  // Fetch SIMs when we reach step 3 (Assign SIMs)
  const fetchSims = useCallback(async () => {
    setLoadingSims(true);
    try {
      const data = await getSims({ limit: 500 });
      setSims(data);
    } catch {
      // silently fail
    } finally {
      setLoadingSims(false);
    }
  }, []);

  // Fetch users when we reach step 5 (Captain Account)
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch {
      // silently fail
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const update = (partial: Partial<WizardData>) => {
    setWizardData(prev => ({ ...prev, ...partial }));
  };

  const handleSelectTemplate = (tmpl: YachtTemplate | null) => {
    if (tmpl) {
      const crewRows: CrewRow[] =
        tmpl.crew_structure?.positions?.map(p => ({
          key: nextCrewKey(),
          role: p.role,
          department: p.department,
          count: p.count,
        })) || [];
      update({
        template: tmpl,
        assetType: tmpl.size_category || '',
        crewStructure: crewRows,
        captainManagesSims: tmpl.management_settings?.captain_manages_sims ?? false,
        captainProtection: tmpl.management_settings?.captain_protection ?? false,
      });
    } else {
      update({
        template: null,
        assetType: '',
        crewStructure: [],
        captainManagesSims: false,
        captainProtection: false,
      });
    }
    setCurrentStep(1);
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      try {
        const values = await detailsForm.validateFields();
        update({
          yachtName: values.name,
          organizationId: values.organizationId,
          assetType: values.asset_type || '',
        });
      } catch {
        return;
      }
    }

    if (currentStep === 3) {
      // Moving from SIMs step — data already in state
    }

    if (currentStep === 5) {
      try {
        if (wizardData.captainMode === 'new') {
          const values = await captainForm.validateFields();
          update({
            captainFirstName: values.firstName,
            captainLastName: values.lastName,
            captainEmail: values.email,
          });
        }
      } catch {
        return;
      }
    }

    // Fetch data for upcoming steps
    if (currentStep + 1 === 3) {
      fetchSims();
    }
    if (currentStep + 1 === 5) {
      fetchUsers();
    }

    setCurrentStep(s => s + 1);
  };

  const handleBack = () => {
    setCurrentStep(s => s - 1);
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      // 1. Create the asset
      const settings: Record<string, any> = {
        captain_manages_sims: wizardData.captainManagesSims,
        captain_protection: wizardData.captainProtection,
      };
      if (wizardData.crewStructure.length > 0) {
        settings.crew_structure = {
          positions: wizardData.crewStructure.map(r => ({
            role: r.role,
            department: r.department,
            count: r.count,
          })),
        };
      }

      const assetRes = await createAsset({
        name: wizardData.yachtName,
        organizationId: wizardData.organizationId!,
        asset_type: wizardData.assetType || undefined,
        settings,
      });

      const assetId = assetRes.id ?? assetRes.asset?.id;
      setCreatedAssetId(assetId);

      // 2. Assign selected SIMs to the asset
      for (const simId of wizardData.selectedSimIds) {
        const sim = sims.find(s => s.identifier === simId);
        if (sim && assetId) {
          try {
            await assignSimToAsset({
              identifier: sim.identifier,
              provider: sim.provider,
              assetId,
            });
          } catch {
            // continue with remaining SIMs
          }
        }
      }

      // 3. Create captain user if new
      if (wizardData.captainMode === 'new' && wizardData.captainEmail) {
        try {
          await createUser({
            email: wizardData.captainEmail,
            firstName: wizardData.captainFirstName,
            lastName: wizardData.captainLastName,
            role: 'asset_manager',
            organizationId: wizardData.organizationId!,
          });
        } catch {
          message.warning(
            'Asset created but captain user creation failed. You can add the captain manually.'
          );
        }
      }

      setCreated(true);
      message.success('Yacht created successfully!');
    } catch (err: any) {
      message.error(err.message || 'Failed to create yacht');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step renderers ─────────────────────────────────────────────────

  const renderTemplateStep = () => (
    <div>
      <Title level={4}>Select a Template</Title>
      <Paragraph>
        Choose a template to pre-fill yacht configuration, or start from scratch.
      </Paragraph>
      {loadingTemplates ? (
        <Spin />
      ) : (
        <Row gutter={[16, 16]}>
          {templates.map(tmpl => (
            <Col xs={24} sm={12} md={8} key={tmpl.id}>
              <Card
                hoverable
                onClick={() => handleSelectTemplate(tmpl)}
                style={{
                  border: wizardData.template?.id === tmpl.id ? '2px solid #1890ff' : undefined,
                }}
              >
                <Card.Meta
                  title={tmpl.name}
                  description={
                    <>
                      {tmpl.description && <Paragraph>{tmpl.description}</Paragraph>}
                      {tmpl.size_category && <Tag color="blue">{tmpl.size_category}</Tag>}
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">SIM slots: {tmpl.yacht_sim_slots}</Text>
                      </div>
                      {tmpl.crew_structure?.positions && (
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary">
                            Crew positions: {tmpl.crew_structure.positions.length}
                          </Text>
                        </div>
                      )}
                    </>
                  }
                />
              </Card>
            </Col>
          ))}
          <Col xs={24} sm={12} md={8}>
            <Card
              hoverable
              onClick={() => handleSelectTemplate(null)}
              style={{
                border:
                  wizardData.template === null && currentStep > 0 ? '2px solid #1890ff' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 150,
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <PlusOutlined style={{ fontSize: 32, color: '#999' }} />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Start from scratch</Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );

  const renderDetailsStep = () => (
    <div>
      <Title level={4}>Yacht Details</Title>
      <Form
        form={detailsForm}
        layout="vertical"
        initialValues={{
          name: wizardData.yachtName,
          organizationId: wizardData.organizationId,
          asset_type: wizardData.assetType,
        }}
        style={{ maxWidth: 500 }}
      >
        <Form.Item
          name="name"
          label="Yacht Name"
          rules={[{ required: true, message: 'Please enter a yacht name' }]}
        >
          <Input placeholder="e.g. MY Serenity" />
        </Form.Item>
        <Form.Item
          name="organizationId"
          label="Customer organization"
          tooltip="Yachts belong to the customer org that operates them. Platform and reseller orgs are filtered out."
          rules={[{ required: true, message: 'Please select a customer organization' }]}
        >
          <Select
            placeholder="Select customer organization"
            loading={loadingOrgs}
            showSearch
            optionFilterProp="children"
          >
            {organizations
              .filter(org => org.type === 'customer' && org.is_active)
              .map(org => (
                <Select.Option key={org.id} value={org.id}>
                  {org.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item name="asset_type" label="Asset Type">
          <LookupSelect category="asset_type" placeholder="Select or enter asset type" />
        </Form.Item>
      </Form>
    </div>
  );

  const renderCrewStep = () => {
    const updateCrewRow = (key: string, field: keyof CrewRow, value: any) => {
      update({
        crewStructure: wizardData.crewStructure.map(r =>
          r.key === key ? { ...r, [field]: value } : r
        ),
      });
    };

    const addCrewRow = () => {
      update({
        crewStructure: [
          ...wizardData.crewStructure,
          { key: nextCrewKey(), role: '', department: '', count: 1 },
        ],
      });
    };

    const removeCrewRow = (key: string) => {
      update({
        crewStructure: wizardData.crewStructure.filter(r => r.key !== key),
      });
    };

    const crewColumns = [
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        render: (_: any, record: CrewRow) => (
          <Input
            value={record.role}
            onChange={e => updateCrewRow(record.key, 'role', e.target.value)}
            placeholder="e.g. Captain"
          />
        ),
      },
      {
        title: 'Department',
        dataIndex: 'department',
        key: 'department',
        render: (_: any, record: CrewRow) => (
          <LookupSelect
            category="department"
            value={record.department}
            onChange={val => updateCrewRow(record.key, 'department', val)}
            placeholder="Select department"
          />
        ),
      },
      {
        title: 'Count',
        dataIndex: 'count',
        key: 'count',
        width: 100,
        render: (_: any, record: CrewRow) => (
          <InputNumber
            min={1}
            value={record.count}
            onChange={val => updateCrewRow(record.key, 'count', val ?? 1)}
          />
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 60,
        render: (_: any, record: CrewRow) => (
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeCrewRow(record.key)}
          />
        ),
      },
    ];

    return (
      <div>
        <Title level={4}>Crew Structure</Title>
        <Paragraph>Define the crew positions for this yacht.</Paragraph>
        <Table
          columns={crewColumns}
          dataSource={wizardData.crewStructure}
          rowKey="key"
          pagination={false}
          size="small"
          footer={() => (
            <Button type="dashed" onClick={addCrewRow} icon={<PlusOutlined />}>
              Add Position
            </Button>
          )}
        />
      </div>
    );
  };

  const [simSearch, setSimSearch] = useState('');
  const [simProviderFilter, setSimProviderFilter] = useState<string | undefined>(undefined);

  const renderSimsStep = () => {
    let availableSims = sims.filter(
      s =>
        !s.allocation ||
        (wizardData.organizationId &&
          s.allocation.organizationId === wizardData.organizationId &&
          !s.allocation.assetId)
    );

    // Apply provider filter
    if (simProviderFilter) {
      availableSims = availableSims.filter(s => s.provider === simProviderFilter);
    }

    // Apply search filter
    if (simSearch) {
      const q = simSearch.toLowerCase();
      availableSims = availableSims.filter(
        s =>
          s.identifier.toLowerCase().includes(q) ||
          (s.msisdn || '').toLowerCase().includes(q) ||
          (s.alias || '').toLowerCase().includes(q)
      );
    }

    const recommendedSlots = wizardData.template?.yacht_sim_slots ?? 0;

    return (
      <div>
        <Title level={4}>Assign Yacht SIMs</Title>
        {recommendedSlots > 0 && (
          <Paragraph>
            Template recommends <Tag color="blue">{recommendedSlots}</Tag> SIM slots. Selected:{' '}
            <Tag color={wizardData.selectedSimIds.length === recommendedSlots ? 'green' : 'orange'}>
              {wizardData.selectedSimIds.length}
            </Tag>
          </Paragraph>
        )}
        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="Provider"
            allowClear
            style={{ width: 150 }}
            value={simProviderFilter}
            onChange={val => setSimProviderFilter(val)}
          >
            <Select.Option value="vrs">Data</Select.Option>
            <Select.Option value="giacom">Crew</Select.Option>
          </Select>
          <Input.Search
            placeholder="Search by ICCID, MSISDN, alias..."
            value={simSearch}
            onChange={e => setSimSearch(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
        </Space>
        {loadingSims ? (
          <Spin />
        ) : (
          <Table
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: wizardData.selectedSimIds,
              onChange: keys => update({ selectedSimIds: keys as string[] }),
            }}
            columns={[
              {
                title: 'Provider',
                dataIndex: 'provider',
                key: 'provider',
                render: (v: string) => <Tag color={v === 'vrs' ? 'blue' : 'green'}>{v}</Tag>,
              },
              {
                title: 'Identifier',
                dataIndex: 'identifier',
                key: 'identifier',
                sorter: (a: UnifiedSim, b: UnifiedSim) => a.identifier.localeCompare(b.identifier),
              },
              {
                title: 'MSISDN',
                dataIndex: 'msisdn',
                key: 'msisdn',
                sorter: (a: UnifiedSim, b: UnifiedSim) =>
                  (a.msisdn || '').localeCompare(b.msisdn || ''),
                render: (v: string | null) => v || '-',
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                filters: [
                  { text: 'Active', value: 'active' },
                  { text: 'Suspended', value: 'suspended' },
                ],
                onFilter: (value: any, record: UnifiedSim) => record.status === value,
                render: (v: string) => (
                  <Tag color={v === 'active' ? 'green' : v === 'suspended' ? 'orange' : 'default'}>
                    {v}
                  </Tag>
                ),
              },
              {
                title: 'Alias',
                dataIndex: 'alias',
                key: 'alias',
                sorter: (a: UnifiedSim, b: UnifiedSim) =>
                  (a.alias || '').localeCompare(b.alias || ''),
                render: (v: string | null) => v || '-',
              },
              {
                title: 'Allocated To',
                key: 'allocation',
                render: (_: any, record: UnifiedSim) => record.allocation?.orgName || 'Unallocated',
              },
            ]}
            dataSource={availableSims}
            rowKey="identifier"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} of ${total}`,
            }}
            size="small"
          />
        )}
      </div>
    );
  };

  const renderPermissionsStep = () => (
    <div>
      <Title level={4}>Configure Permissions</Title>
      <Paragraph>Set yacht management permissions.</Paragraph>
      <div style={{ maxWidth: 500 }}>
        <div style={{ marginBottom: 16 }}>
          <Checkbox
            checked={wizardData.captainManagesSims}
            onChange={e => update({ captainManagesSims: e.target.checked })}
          >
            Captain manages SIMs
          </Checkbox>
          <div style={{ marginLeft: 24 }}>
            <Text type="secondary">
              Allow the captain to allocate and manage SIM assignments for their yacht.
            </Text>
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Checkbox
            checked={wizardData.captainProtection}
            onChange={e => update({ captainProtection: e.target.checked })}
          >
            Captain protection
          </Checkbox>
          <div style={{ marginLeft: 24 }}>
            <Text type="secondary">
              Prevent lower roles from viewing the captain's SIM usage data.
            </Text>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCaptainStep = () => {
    const orgUsers = users.filter(
      u => wizardData.organizationId && u.organization_id === wizardData.organizationId
    );

    return (
      <div>
        <Title level={4}>Captain Account</Title>
        <Paragraph>Create a new captain or select an existing user.</Paragraph>
        <div style={{ maxWidth: 500 }}>
          <Select
            value={wizardData.captainMode}
            onChange={(val: 'new' | 'existing') => update({ captainMode: val })}
            style={{ marginBottom: 16, width: 250 }}
          >
            <Select.Option value="new">Create new captain</Select.Option>
            <Select.Option value="existing">Select existing user</Select.Option>
          </Select>

          {wizardData.captainMode === 'new' ? (
            <Form
              form={captainForm}
              layout="vertical"
              initialValues={{
                firstName: wizardData.captainFirstName,
                lastName: wizardData.captainLastName,
                email: wizardData.captainEmail,
              }}
            >
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="First name" />
              </Form.Item>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="Last name" />
              </Form.Item>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'email', message: 'Invalid email' },
                ]}
              >
                <Input placeholder="captain@example.com" />
              </Form.Item>
            </Form>
          ) : (
            <div>
              {loadingUsers ? (
                <Spin />
              ) : (
                <Select
                  value={wizardData.existingCaptainId}
                  onChange={val => update({ existingCaptainId: val })}
                  placeholder="Select a user"
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  options={orgUsers.map(u => ({
                    value: u.id,
                    label: `${u.first_name} ${u.last_name} (${u.email})`,
                  }))}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    if (created) {
      return (
        <Result
          status="success"
          title="Yacht Created Successfully"
          subTitle={`"${wizardData.yachtName}" has been set up with ${wizardData.selectedSimIds.length} SIM(s) and ${wizardData.crewStructure.length} crew position(s).`}
          extra={[
            createdAssetId ? (
              <Button
                type="primary"
                key="view"
                onClick={() => navigate(`/admin/assets/${createdAssetId}`)}
              >
                View Asset
              </Button>
            ) : null,
            <Button key="list" onClick={() => navigate('/admin/orgs-assets')}>
              Back to Org &amp; Assets
            </Button>,
          ].filter(Boolean)}
        />
      );
    }

    const selectedOrg = organizations.find(o => o.id === wizardData.organizationId);
    const selectedCaptain = users.find(u => u.id === wizardData.existingCaptainId);

    return (
      <div>
        <Title level={4}>Review & Create</Title>
        <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Template">
            {wizardData.template?.name || 'None (from scratch)'}
          </Descriptions.Item>
          <Descriptions.Item label="Yacht Name">{wizardData.yachtName}</Descriptions.Item>
          <Descriptions.Item label="Organization">
            {selectedOrg?.name || String(wizardData.organizationId)}
          </Descriptions.Item>
          <Descriptions.Item label="Asset Type">{wizardData.assetType || '-'}</Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">
          Crew Structure ({wizardData.crewStructure.length} positions)
        </Divider>
        {wizardData.crewStructure.length > 0 ? (
          <Table
            columns={[
              { title: 'Role', dataIndex: 'role', key: 'role' },
              { title: 'Department', dataIndex: 'department', key: 'department' },
              { title: 'Count', dataIndex: 'count', key: 'count' },
            ]}
            dataSource={wizardData.crewStructure}
            rowKey="key"
            pagination={false}
            size="small"
            style={{ marginBottom: 24 }}
          />
        ) : (
          <Paragraph type="secondary">No crew positions defined.</Paragraph>
        )}

        <Divider orientation="left">SIMs ({wizardData.selectedSimIds.length} selected)</Divider>
        {wizardData.selectedSimIds.length > 0 ? (
          <Space wrap>
            {wizardData.selectedSimIds.map(id => (
              <Tag key={id}>{id}</Tag>
            ))}
          </Space>
        ) : (
          <Paragraph type="secondary">No SIMs selected.</Paragraph>
        )}

        <Divider orientation="left">Permissions</Divider>
        <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Captain manages SIMs">
            {wizardData.captainManagesSims ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Captain protection">
            {wizardData.captainProtection ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">Captain</Divider>
        <Descriptions bordered column={1} size="small">
          {wizardData.captainMode === 'new' ? (
            <>
              <Descriptions.Item label="Mode">Create new user</Descriptions.Item>
              <Descriptions.Item label="Name">
                {wizardData.captainFirstName} {wizardData.captainLastName}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{wizardData.captainEmail}</Descriptions.Item>
              <Descriptions.Item label="Role">
                <Tag color="blue">asset_manager</Tag>
              </Descriptions.Item>
            </>
          ) : (
            <>
              <Descriptions.Item label="Mode">Existing user</Descriptions.Item>
              <Descriptions.Item label="User">
                {selectedCaptain
                  ? `${selectedCaptain.first_name} ${selectedCaptain.last_name} (${selectedCaptain.email})`
                  : '-'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </div>
    );
  };

  // ─── Steps configuration ────────────────────────────────────────────

  const steps = [
    { title: 'Template' },
    { title: 'Details' },
    { title: 'Crew' },
    { title: 'SIMs' },
    { title: 'Permissions' },
    { title: 'Captain' },
    { title: 'Review' },
  ];

  const stepContent: Record<number, React.ReactNode> = {
    0: renderTemplateStep(),
    1: renderDetailsStep(),
    2: renderCrewStep(),
    3: renderSimsStep(),
    4: renderPermissionsStep(),
    5: renderCaptainStep(),
    6: renderReviewStep(),
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        New Yacht Onboarding
      </Title>

      <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />

      <Card style={{ minHeight: 300, marginBottom: 24 }}>{stepContent[currentStep]}</Card>

      {!created && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>{currentStep > 0 && <Button onClick={handleBack}>Back</Button>}</div>
          <div>
            {currentStep < 6 && currentStep > 0 && (
              <Button type="primary" onClick={handleNext}>
                Next
              </Button>
            )}
            {currentStep === 6 && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={submitting}
                onClick={handleCreate}
              >
                Create Yacht
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default YachtOnboarding;
